// Collection Day Sheet — two lenses on the same date:
//
//   • Due       — every subscription whose due_date is this IST calendar day.
//                 Forward-looking ("who must I collect from today?"), with
//                 prior-arrears carry-over per subscriber.
//
//   • Collected — every payment whose timestamp is this IST calendar day.
//                 Retrospective ("who paid me today, against what?"). One row
//                 per payment, with payment method + reference + which
//                 due_month it paid against.
//
// The Day Book also shows today's payments but as a cash-vs-bank reconciliation
// view (opening / closing / disbursements). This report is the per-member view.

import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { current_language } from '../i18n/index.js';
import { format_inr, short_month_label, today_ymd_ist } from '../lib/format.js';
import { friendly_error } from '../lib/friendly-error.js';
import { orpc } from '../lib/orpc.js';
import { Icon } from '../components/Icon.js';
import { DateFilterBar } from '../components/DateFilterBar.js';

type Mode = 'due' | 'collected';

interface DueRow {
  subscription_id: string;
  subscriber_id: string;
  chit_id: string;
  subscriber: { name_ta: string; name_en: string; phone: string; town_ta: string | null; town_en: string | null };
  chit: { name_ta: string; name_en: string; slot_number: number | null };
  due_month: string;
  expected_paise: number;
  paid_paise: number;
  outstanding_paise: number;
  prior_arrears_paise: number;
  status: string;
}

interface CollectedRow {
  payment_id: string;
  subscription_id: string;
  subscriber_id: string;
  chit_id: string;
  subscriber: { name_ta: string; name_en: string; phone: string; town_ta: string | null; town_en: string | null };
  chit: { name_ta: string; name_en: string; slot_number: number | null };
  due_month: string;
  paid_paise: number;
  method: string;
  reference: string | null;
  timestamp: string;
}

interface DueData {
  mode: 'due';
  date: string;
  rows: DueRow[];
  total_expected_paise: number;
  total_paid_paise: number;
  total_outstanding_paise: number;
  total_prior_arrears_paise: number;
  count_partial: number;
  count_unpaid: number;
  count_paid: number;
}

interface CollectedData {
  mode: 'collected';
  date: string;
  rows: CollectedRow[];
  total_collected_paise: number;
  count_payments: number;
  unique_subscribers: number;
  by_method: Array<{ method: string; total_paise: number; count: number }>;
}

type Data = DueData | CollectedData;

const METHOD_COLOR: Record<string, string> = {
  upi: 'var(--primary)',
  cash: 'var(--good)',
  cheque: 'var(--warn)',
  neft: 'var(--info)',
  bank_transfer: 'var(--ink-3)',
};

export function ReportsCollectionDayPage(): ReactElement {
  const lang = current_language();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const [date, set_date] = useState(today_ymd_ist());
  const [mode, set_mode] = useState<Mode>('due');
  const [group_by, set_group_by] = useState<'chit' | 'town'>('chit');

  const q = useQuery({
    queryKey: ['report-collection-day', date, mode],
    queryFn: () => orpc.reports.collection_day_sheet({ date, mode }) as Promise<Data>,
  });
  const data = q.data;

  // ── Grouped views (Due mode only) ─────────────────────────────────────────
  const grouped_due = useMemo(() => {
    if (!data || data.mode !== 'due') return [];
    const buckets = new Map<string, DueRow[]>();
    for (const r of data.rows) {
      const key = group_by === 'chit'
        ? (lang === 'ta' ? r.chit.name_ta : r.chit.name_en) || '—'
        : ((lang === 'ta' ? r.subscriber.town_ta : r.subscriber.town_en) ?? '—');
      const list = buckets.get(key) ?? [];
      list.push(r);
      buckets.set(key, list);
    }
    return Array.from(buckets.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [data, group_by, lang]);

  // ── CSV export (mode-aware) ───────────────────────────────────────────────
  const export_csv = (): void => {
    if (!data) return;
    let lines: string[];
    if (data.mode === 'due') {
      lines = [
        'subscription_id,subscriber_id,chit_id,subscriber_name,phone,town,chit,slot,due_month,expected,paid,outstanding,prior_arrears,status',
        ...data.rows.map((r) =>
          [
            r.subscription_id,
            r.subscriber_id,
            r.chit_id,
            (r.subscriber.name_en || r.subscriber.name_ta).replace(/,/g, ' '),
            r.subscriber.phone,
            (r.subscriber.town_en || r.subscriber.town_ta || '').replace(/,/g, ' '),
            (r.chit.name_en || r.chit.name_ta).replace(/,/g, ' '),
            r.chit.slot_number ?? '',
            r.due_month,
            (r.expected_paise / 100).toFixed(2),
            (r.paid_paise / 100).toFixed(2),
            (r.outstanding_paise / 100).toFixed(2),
            (r.prior_arrears_paise / 100).toFixed(2),
            r.status,
          ].join(','),
        ),
      ];
    } else {
      lines = [
        'payment_id,timestamp,subscriber_id,chit_id,subscriber_name,phone,town,chit,slot,paid_against_due_month,method,reference,amount',
        ...data.rows.map((r) =>
          [
            r.payment_id,
            r.timestamp,
            r.subscriber_id,
            r.chit_id,
            (r.subscriber.name_en || r.subscriber.name_ta).replace(/,/g, ' '),
            r.subscriber.phone,
            (r.subscriber.town_en || r.subscriber.town_ta || '').replace(/,/g, ' '),
            (r.chit.name_en || r.chit.name_ta).replace(/,/g, ' '),
            r.chit.slot_number ?? '',
            r.due_month,
            r.method,
            (r.reference ?? '').replace(/,/g, ' '),
            (r.paid_paise / 100).toFixed(2),
          ].join(','),
        ),
      ];
    }
    const blob = new Blob(['﻿', lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `collection-day-${date}-${mode}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [preview, set_preview] = useState<string | null>(null);
  const pdf_m = useMutation({
    mutationFn: () => orpc.reports.collection_day_pdf({ date, mode }),
    onSuccess: (out) => {
      if (out.preview_html) set_preview(out.preview_html);
      else window.open(out.pdf_url, '_blank');
    },
  });

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link to="/reports" style={{ color: 'var(--ink-3)', textDecoration: 'none', fontSize: 12 }}>
              ← {t('அறிக்கைகள்', 'Reports')}
            </Link>
          </div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>
            {t('வசூல் நாள் பட்டியல்', 'Collection Day Sheet')}
          </div>
          <div className="h-sub">{date} · {mode === 'due' ? t('நிலுவையில் உள்ளவை', 'Due on this date') : t('இன்று வசூலிக்கப்பட்டவை', 'Collected on this date')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <DateFilterBar mode="single" value={date} on_change={set_date} />
          <button type="button" className="btn" onClick={export_csv} disabled={!data || data.rows.length === 0}>
            <Icon.download /> CSV
          </button>
          <button type="button" className="btn primary" onClick={() => pdf_m.mutate()} disabled={pdf_m.isPending}>
            <Icon.download /> {pdf_m.isPending ? '…' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Mode toggle — "View as" */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {t('காட்சி', 'View as')}
        </span>
        <button type="button" className={'btn sm' + (mode === 'due' ? ' primary' : '')} onClick={() => set_mode('due')}>
          {t('நிலுவை', 'Due')}
        </button>
        <button type="button" className={'btn sm' + (mode === 'collected' ? ' primary' : '')} onClick={() => set_mode('collected')}>
          {t('வசூலிக்கப்பட்டது', 'Collected')}
        </button>
        <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 8 }}>
          {mode === 'due'
            ? t('இந்த நாள் வசூலிக்க வேண்டியவை', "Subscriptions whose due_date is this day")
            : t('இந்த நாள் வசூலிக்கப்பட்டவை', 'Payments whose timestamp is this day')}
        </span>
      </div>

      {/* KPI strip — mode-dependent */}
      {data?.mode === 'due' && (
        <div className="kpis" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          <div className="kpi"><div className="kpi-label">{t('எதிர்பார்ப்பு', 'Expected')}</div><div className="kpi-value">{format_inr(data.total_expected_paise)}</div></div>
          <div className="kpi"><div className="kpi-label">{t('வசூலானது', 'Collected')}</div><div className="kpi-value">{format_inr(data.total_paid_paise)}</div></div>
          <div className={'kpi' + (data.total_outstanding_paise > 0 ? ' warn' : '')}>
            <div className="kpi-label">{t('நிலுவை', 'Outstanding')}</div><div className="kpi-value">{format_inr(data.total_outstanding_paise)}</div>
            <div className="kpi-sub">{`${data.count_partial} ${t('பகுதி', 'partial')} · ${data.count_unpaid} ${t('பாக்கி', 'unpaid')}`}</div>
          </div>
          <div className={'kpi' + (data.total_prior_arrears_paise > 0 ? ' warn' : '')}>
            <div className="kpi-label">{t('முந்தைய பாக்கி', 'Prior arrears')}</div><div className="kpi-value">{format_inr(data.total_prior_arrears_paise)}</div>
          </div>
        </div>
      )}
      {data?.mode === 'collected' && (
        <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="kpi primary"><div className="kpi-label">{t('மொத்த வசூல்', 'Total collected')}</div><div className="kpi-value">{format_inr(data.total_collected_paise)}</div></div>
          <div className="kpi"><div className="kpi-label">{t('கொடுப்பனவுகள்', 'Payments')}</div><div className="kpi-value">{data.count_payments}</div></div>
          <div className="kpi"><div className="kpi-label">{t('உறுப்பினர்கள்', 'Unique members')}</div><div className="kpi-value">{data.unique_subscribers}</div></div>
        </div>
      )}

      {/* Method strip (Collected mode only) */}
      {data?.mode === 'collected' && data.by_method.length > 0 && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13 }}>
            {data.by_method.map((m) => (
              <div key={m.method} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: METHOD_COLOR[m.method] ?? 'var(--ink-3)' }} />
                <span className="mono" style={{ textTransform: 'uppercase', fontSize: 11 }}>{m.method}</span>
                <span className="mono"><strong>{format_inr(m.total_paise)}</strong></span>
                <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>({m.count})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Group-by toggle (Due mode only) */}
      {data?.mode === 'due' && data.rows.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {t('குழுவாக்கம்', 'Group by')}
          </span>
          <button type="button" className={'btn sm' + (group_by === 'chit' ? ' primary' : '')} onClick={() => set_group_by('chit')}>
            {t('சீட்டு', 'Chit')}
          </button>
          <button type="button" className={'btn sm' + (group_by === 'town' ? ' primary' : '')} onClick={() => set_group_by('town')}>
            {t('ஊர்', 'Town')}
          </button>
        </div>
      )}

      {q.isLoading && <div style={{ color: 'var(--ink-3)', padding: 20 }}>{t('ஏற்றுகிறது…', 'Loading…')}</div>}
      {q.error && <div style={{ color: 'var(--bad)', padding: 20 }}>{friendly_error(q.error)}</div>}
      {data && data.rows.length === 0 && (
        <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--ink-3)' }}>
          {data.mode === 'due'
            ? t('இந்த நாளில் வசூல் இல்லை', 'No subscriptions due on this date')
            : t('இந்த நாளில் கொடுப்பனவுகள் இல்லை', 'No payments collected on this date')}
        </div>
      )}

      {/* Due mode — grouped tables */}
      {data?.mode === 'due' && grouped_due.map(([group_key, group_rows]) => (
        <div key={group_key} className="card" style={{ marginBottom: 12 }}>
          <div className="card-head">
            <div className={'card-title ' + (lang === 'ta' ? 'tamil' : '')}>{group_key}</div>
            <div className="card-sub mono">{group_rows.length} {t('உறுப்பினர்', 'members')}</div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: 'var(--bg-elev)' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>#</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('உறுப்பினர்', 'Member')}</th>
                  {group_by === 'chit' && <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('ஊர்', 'Town')}</th>}
                  {group_by === 'town' && <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('சீட்டு', 'Chit')}</th>}
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('எதிர்பார்ப்பு', 'Expected')}</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('பெற்றது', 'Paid')}</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('நிலுவை', 'Outstanding')}</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('பாக்கி', 'Arrears')}</th>
                </tr>
              </thead>
              <tbody>
                {group_rows.map((r, i) => {
                  const danger = r.status !== 'paid';
                  return (
                    <tr key={r.subscription_id} style={{ borderTop: '1px solid var(--line)', background: danger && r.status === 'overdue' ? 'var(--bad-soft)' : undefined }}>
                      <td style={{ padding: '6px 10px', color: 'var(--ink-3)' }}>{i + 1}</td>
                      <td style={{ padding: '6px 10px' }} className={lang === 'ta' ? 'tamil' : ''}>
                        <Link to={`/subscribers/${r.subscriber_id}`} style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                          {(lang === 'ta' ? r.subscriber.name_ta : r.subscriber.name_en) || '—'}
                        </Link>
                        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.subscriber.phone}</div>
                      </td>
                      {group_by === 'chit' && <td style={{ padding: '6px 10px' }} className={lang === 'ta' ? 'tamil' : ''}>{(lang === 'ta' ? r.subscriber.town_ta : r.subscriber.town_en) ?? '—'}</td>}
                      {group_by === 'town' && <td style={{ padding: '6px 10px' }} className={lang === 'ta' ? 'tamil' : ''}>{(lang === 'ta' ? r.chit.name_ta : r.chit.name_en) || '—'}{r.chit.slot_number ? <span style={{ color: 'var(--ink-3)', fontSize: 11 }}> #{r.chit.slot_number}</span> : null}</td>}
                      <td style={{ padding: '6px 10px', textAlign: 'right' }} className="mono">{format_inr(r.expected_paise)}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right' }} className="mono">{format_inr(r.paid_paise)}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: r.outstanding_paise > 0 ? 'var(--bad)' : 'var(--ink-3)' }} className="mono">{format_inr(r.outstanding_paise)}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: r.prior_arrears_paise > 0 ? 'var(--warn)' : 'var(--ink-3)' }} className="mono">{r.prior_arrears_paise > 0 ? format_inr(r.prior_arrears_paise) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Collected mode — flat chronological table */}
      {data?.mode === 'collected' && data.rows.length > 0 && (
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: 'var(--bg-elev)' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>#</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('நேரம்', 'Time')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('உறுப்பினர்', 'Member')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('சீட்டு', 'Chit')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('மாதம்', 'For month')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('வழி', 'Method')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('குறிப்பு', 'Reference')}</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('தொகை', 'Amount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={r.payment_id} style={{ borderTop: '1px solid var(--line)' }}>
                    <td style={{ padding: '6px 10px', color: 'var(--ink-3)' }}>{i + 1}</td>
                    <td style={{ padding: '6px 10px' }} className="mono">
                      {new Date(r.timestamp).toLocaleTimeString(lang === 'ta' ? 'ta-IN' : 'en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '6px 10px' }} className={lang === 'ta' ? 'tamil' : ''}>
                      <Link to={`/subscribers/${r.subscriber_id}`} style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                        {(lang === 'ta' ? r.subscriber.name_ta : r.subscriber.name_en) || '—'}
                      </Link>
                      <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.subscriber.phone}</div>
                    </td>
                    <td style={{ padding: '6px 10px' }} className={lang === 'ta' ? 'tamil' : ''}>
                      <Link to={`/chits/${r.chit_id}`} style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                        {(lang === 'ta' ? r.chit.name_ta : r.chit.name_en) || '—'}
                      </Link>
                      {r.chit.slot_number ? <span style={{ color: 'var(--ink-3)', fontSize: 11 }}> #{r.chit.slot_number}</span> : null}
                    </td>
                    <td style={{ padding: '6px 10px', color: 'var(--ink-3)' }}>{short_month_label(r.due_month, lang)} {r.due_month.slice(0, 4)}</td>
                    <td style={{ padding: '6px 10px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: METHOD_COLOR[r.method] ?? 'var(--ink-3)' }} />
                        <span className="mono" style={{ textTransform: 'uppercase', fontSize: 11 }}>{r.method}</span>
                      </span>
                    </td>
                    <td style={{ padding: '6px 10px', color: 'var(--ink-3)', fontSize: 11 }}>{r.reference ?? '—'}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--good)', fontWeight: 600 }} className="mono">{format_inr(r.paid_paise)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--line)', background: 'var(--bg-elev)' }}>
                  <td colSpan={7} style={{ padding: '8px 10px', fontWeight: 600 }}>{t('மொத்தம்', 'Total')}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }} className="mono">{format_inr(data.total_collected_paise)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {pdf_m.error && (
        <div style={{ color: 'var(--bad)', padding: 10 }}>{friendly_error(pdf_m.error)}</div>
      )}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,24,20,0.45)', zIndex: 10000, padding: 16, overflow: 'auto' }}>
          <div style={{ maxWidth: 960, margin: 'auto', background: 'white', borderRadius: 8 }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>PDF preview (DRY_RUN)</div>
              <button type="button" className="btn sm" onClick={() => set_preview(null)}>Close</button>
            </div>
            <iframe title="cd-preview" srcDoc={preview} style={{ width: '100%', height: '80vh', border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}
